import { useAppSelector } from "lib/redux/hooks";
import { selectIsPremiumUser } from "lib/redux/uiSlice";
import Image from "next/image";
import Link from "next/link";
import { ReactNode, useState } from "react";
import { PremiumCard } from "./PermiumCard";

interface SectionCardProps {
  title: string;
  icon: string;
  score: number;
  maxScore: number;
  detailsLink: string;
  requiresPremium?: boolean;
  className?: string;
  children: ReactNode;
  emptyState?: boolean;
  onAddClick?: () => void;
}

export const SectionCard = ({
  title,
  icon,
  score,
  maxScore,
  detailsLink,
  requiresPremium = false,
  className = "",
  children,
  emptyState = false,
  onAddClick,
}: SectionCardProps) => {
  const hasPremium = useAppSelector(selectIsPremiumUser);
  const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);

  const handleCardClick = () => {
    if (requiresPremium && !hasPremium) {
      setShowPremiumOverlay(true);
    }
  };

  const scoreRatio = score / maxScore;
  const scoreColorClass = 
    scoreRatio > 0.75 ? 'text-green-800' : 
    scoreRatio >= 0.4 ? 'text-orange-700' : 
    'text-[#BB0808]';

  return (
    <div className={`relative h-full ${className}`}>
      <Link 
        href={requiresPremium && !hasPremium ? "#" : detailsLink}
        onClick={handleCardClick}
        className={`group cursor-pointer h-full min-h-[360px] transition-all duration-500 ease-in-out flex max-h-full flex-col justify-between rounded-xl border border-purple-200 bg-gray-50 p-4 shadow-md ${
          hasPremium || !requiresPremium ? "hover:bg-[#6369a2]" : ""
        }`}
      >
        <div className="flex h-full w-full flex-col">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex flex-col items-start justify-start">
              <div className="flex items-center justify-start gap-2">
                <Image
                  className="group-hover:hidden"
                  src={`/icons/${icon}Icon.svg`}
                  alt={`${title} icon`}
                  width={23}
                  height={23}
                />
                <Image
                  className="hidden group-hover:block"
                  src={`/icons/${icon}IconDark.svg`}
                  alt={`${title} icon`}
                  width={23}
                  height={23}
                />
                <span className="text-[22px] font-medium group-hover:text-white">
                  {title}
                </span>
              </div>
              <span className="text-[12px] text-[#979BA4] group-hover:text-white">
                Essential section
              </span>
            </div>
            <span className="rounded-[7px] bg-[#7EB3F8] px-3 py-1 text-[16px] font-medium text-white group-hover:bg-white group-hover:text-[#458ef7]">
              <span className={scoreColorClass}>
                {score}
              </span>
              /{maxScore}
            </span>
          </div>
          
          {emptyState ? (
            <Link 
              href={detailsLink} 
              className="flex h-full w-full justify-center items-center my-4"
              onClick={(e) => {
                e.stopPropagation();
                onAddClick && onAddClick();
              }}
            >
              <button className="rounded-lg my-20 bg-gradient-to-r from-[#113BE1] to-[#8DA4FF] px-8 py-2 text-lg font-medium text-white shadow-md hover:shadow-lg group-hover:from-white group-hover:to-white group-hover:text-black transition-all duration-200">
                ADD
              </button>
            </Link>
          ) : (
            <>
              <div className="flex-1 overflow-auto">
                {children}
              </div>
              <Link 
                href={detailsLink} 
                className="mt-4 block"
                onClick={(e) => e.stopPropagation()}
              >
                <button className="w-full rounded-lg bg-gradient-to-r from-[#113BE1] to-[#8DA4FF] py-2 font-medium text-white group-hover:from-white group-hover:to-white group-hover:text-black">
                  View
                </button>
              </Link>
            </>
          )}
        </div>
      </Link>
      
      {requiresPremium && !hasPremium && (
        <div className={`absolute h-full inset-0 transition-opacity duration-300 ${showPremiumOverlay ? 'opacity-100' : 'opacity-0 hover:opacity-100'}`}>
          <PremiumCard />
          {showPremiumOverlay && (
            <button
              className="absolute top-2 right-2 bg-white/30 rounded-full p-1"
              onClick={() => setShowPremiumOverlay(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};